/**
 * API endpoints para Purchase Orders (Órdenes de Compra)
 */

import { DELETE, GET, PATCH, POST, PUT } from "../config";

/**
 * Listar órdenes de compra
 */
export const list = async (params = {}) => {
  const urlParams = new URLSearchParams();

  if (params.page) urlParams.append("page", params.page);
  if (params.page_size) urlParams.append("page_size", params.page_size);
  if (params.search) urlParams.append("search", params.search);
  if (params.status) urlParams.append("status", params.status);
  if (params.branch) urlParams.append("branch", params.branch);
  if (params.supplier) urlParams.append("supplier", params.supplier);
  if (params.start_date) urlParams.append("start_date", params.start_date);
  if (params.end_date) urlParams.append("end_date", params.end_date);
  if (params.payment_status)
    urlParams.append("payment_status", params.payment_status);

  const url = urlParams.toString()
    ? `suppliers/purchase-orders/?${urlParams.toString()}`
    : "suppliers/purchase-orders/";

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener detalle de una orden de compra
 */
export const getDetail = (id) => {
  return GET(`suppliers/purchase-orders/${id}/`);
};

/**
 * Crear orden de compra
 */
export const create = (data) => {
  return POST("suppliers/purchase-orders/", data);
};

/**
 * Actualizar orden de compra
 */
export const update = (id, data) => {
  return PUT(`suppliers/purchase-orders/${id}/`, data);
};

/**
 * Actualizar parcialmente orden de compra
 */
export const partialUpdate = (id, data) => {
  return PATCH(`suppliers/purchase-orders/${id}/`, data);
};

/**
 * Eliminar orden de compra
 */
export const remove = (id) => {
  return DELETE(`suppliers/purchase-orders/${id}/`);
};

/**
 * Registrar pago de una orden de compra
 */
export const payOrder = (id, data) => {
  return POST(`suppliers/purchase-orders/${id}/pay_order/`, data);
};

/**
 * Obtener resumen de pagos de una orden (incluye historial)
 */
export const getPaymentSummary = (id) => {
  return GET(`suppliers/purchase-orders/${id}/payment_summary/`);
};

/**
 * Obtener órdenes con pagos pendientes
 */
export const getPendingPayments = (params = {}) => {
  return GET("suppliers/purchase-orders/reports/", {
    params: { ...params, report_type: "pending_payments" },
  });
};

/**
 * Obtener estadísticas de órdenes de compra
 */
export const getStats = async (params = {}) => {
  const urlParams = new URLSearchParams();
  if (params.start_date) urlParams.append("start_date", params.start_date);
  if (params.end_date) urlParams.append("end_date", params.end_date);
  if (params.branch) urlParams.append("branch", params.branch);
  if (params.status) urlParams.append("status", params.status);

  const url = urlParams.toString()
    ? `suppliers/purchase-orders/stats/?${urlParams.toString()}`
    : "suppliers/purchase-orders/stats/";

  const response = await GET(url);
  return response.data;
};

/**
 * Cambiar estado de una orden
 */
export const changeStatus = (id, status) => {
  return PATCH(`suppliers/purchase-orders/${id}/`, { status });
};

/**
 * Cancelar orden de compra (usa el servicio que revierte pagos)
 */
export const cancelOrder = (id, reason = null) => {
  return POST(`suppliers/purchase-orders/${id}/cancel_order/`, { reason });
};

/**
 * Generar PDF de una orden de compra
 * Retorna un blob del PDF que se puede descargar
 */
/**
 * Descargar comprobante de orden de compra (como Revenue/Expense)
 * Retorna un blob del PDF que se puede descargar
 */
export const downloadVoucher = async (id) => {
  const { Axios } = require("../config");
  const token = localStorage.getItem("token");

  try {
    const response = await Axios.get(
      `suppliers/purchase-orders/${id}/download-voucher/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        responseType: "blob", // Importante para recibir el PDF como blob
      }
    );

    return response.data; // Axios ya convierte a blob cuando responseType es "blob"
  } catch (error) {
    // Si el error tiene un mensaje en el response, intentar leerlo
    if (error.response?.data) {
      // Si es un blob con error, intentar leerlo como texto
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorJson = JSON.parse(text);
          throw new Error(errorJson.error || "Error descargando comprobante");
        } catch {
          throw new Error("Error descargando comprobante");
        }
      }
    }
    throw new Error(error.message || "Error descargando comprobante");
  }
};

export const generatePDF = async (id) => {
  const { Axios } = require("../config");
  const token = localStorage.getItem("token");

  try {
    const response = await Axios.get(
      `suppliers/purchase-orders/${id}/generate_pdf/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        responseType: "blob", // Importante para recibir el PDF como blob
      }
    );

    return response.data; // Axios ya convierte a blob cuando responseType es "blob"
  } catch (error) {
    // Si el error tiene un mensaje en el response, intentar leerlo
    if (error.response?.data) {
      // Si es un blob con error, intentar leerlo como texto
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorJson = JSON.parse(text);
          throw new Error(errorJson.error || "Error generando PDF");
        } catch {
          throw new Error("Error generando PDF");
        }
      }
    }
    throw new Error(error.message || "Error generando PDF");
  }
};

/**
 * Marcar orden como completada manualmente
 */
export const markCompleted = (id, data = {}) => {
  return POST(`suppliers/purchase-orders/${id}/mark_completed/`, data);
};

const purchaseOrdersApi = {
  downloadVoucher,
  list,
  getDetail,
  create,
  update,
  partialUpdate,
  remove,
  payOrder,
  getPaymentSummary,
  getPendingPayments,
  changeStatus,
  cancelOrder,
  generatePDF,
  getStats,
  markCompleted, // ✅ Nuevo endpoint
};

export default purchaseOrdersApi;
